import { useGetSellerCustomers } from '../../../api/lending/lapiHooks';
import { TabConfig } from '../CustomersV2.types';
import { useCustomersTableState } from './useCustomersTableState';
import { TableSettings } from './useTableSettings';

/**
 * Whole-tab totals for the active tab.
 *
 * The API narrows `totals_by_*` by every filter it receives — including the
 * pill filter (`application_stage` / `spending_limit_status`). If the counts
 * were read from the same query that drives the table, selecting a pill would
 * collapse every other pill's count to 0. So this fetches from a SEPARATE query
 * scoped to `view` (+ `search`) only, never the pill filter — giving stable,
 * whole-tab counts. The distinct query key keeps it a separate React Query
 * cache entry from the table's query.
 *
 * `include_declined` is sent here too: the counts must agree with the rows, or
 * a hidden declined buyer would be counted in a pill the seller cannot see.
 */
export function useTabTotals(
  config: TabConfig,
  settings: TableSettings,
): {
  counts: Record<string, number> | undefined;
} {
  const { state } = useCustomersTableState(config, settings);

  const { metadata, loadingSellerCustomers, sellerCustomersFailed } =
    useGetSellerCustomers({
      page: 1,
      view: config.view,
      search: state.search ?? undefined,
      include_declined: settings.declined || undefined,
    });

  const counts =
    config.filterParam === 'application_stage'
      ? metadata?.totals_by_application_stage
      : metadata?.totals_by_spending_limit_status;

  // `undefined` means the counts aren't known — still in flight, or the request
  // failed terminally. Callers must not read a missing key as zero in either
  // case; once this is an object, an absent stage genuinely has no rows.
  if (loadingSellerCustomers || sellerCustomersFailed) {
    return { counts: undefined };
  }

  return { counts: (counts ?? {}) as Record<string, number> };
}
